import { useMemo } from 'react';

import { isGroupEventItem, isRenderableActivity } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { ActivityFeedItemDto } from '@splitfiesta/shared';
import { Avatar, Button, Card } from '@splitfiesta/ui';

import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { ExpenseCard } from '@/components/expense/ExpenseCard';
import { SupporterBadge } from '@/components/pro/SupporterBadge';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { RelativeTime } from '@/components/ui/RelativeTime';

import { resolveUserName } from '@/lib/displayName';

import type { ExpenseFilterMode } from '@/stores/uiStore';

import { ActivityEventCard } from './ActivityEventCard';

interface ActivityFeedPresentationProps {
  activities: ActivityFeedItemDto[];
  initialLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  expandedComments: Set<string>;
  loadingComments: { [key: string]: boolean };
  loadingReaction: { [key: string]: boolean };
  lastActivityRef: (node: HTMLElement | null) => void;
  /** Shown when pages came back empty but the server has more: nothing is left to scroll to. */
  onLoadMore?: () => void;
  showTitle?: boolean;
  filterMode?: ExpenseFilterMode;
  onFilterChange?: (value: ExpenseFilterMode) => void;
  onAddReaction: (activityId: string, emoji: string) => Promise<void>;
  onToggleReaction: (
    activityId: string,
    emoji: string,
    hasReacted: boolean
  ) => Promise<void>;
  onSubmitComment: (activityId: string, text: string) => Promise<void>;
  onLoadAllComments: (activityId: string) => Promise<void>;
  onEditComment: (
    activityId: string,
    commentId: string,
    newText: string
  ) => Promise<void>;
  onDeleteComment: (activityId: string, commentId: string) => Promise<void>;
  /** This surface shows Supporter badges; the badge checks the `pro` flag. */
  showSupporterBadges?: boolean;
}

/**
 * Presentation component for ActivityFeed
 * Handles pure rendering without business logic
 */
export function ActivityFeedPresentation({
  activities,
  initialLoading,
  isFetchingNextPage,
  hasNextPage,
  expandedComments,
  loadingComments,
  loadingReaction,
  lastActivityRef,
  onLoadMore,
  showTitle = true,
  filterMode = 'all',
  onFilterChange,
  onAddReaction,
  onToggleReaction,
  onSubmitComment,
  onLoadAllComments,
  onEditComment,
  onDeleteComment,
  showSupporterBadges = false,
}: ActivityFeedPresentationProps) {
  const { t } = useTranslation();

  // Load required translation namespaces
  useNamespace('dashboard');
  useNamespace('expenses');
  useNamespace('common');
  useNamespace('social');

  // An event of a type this build does not know is dropped before the list,
  // so the last drawn row still carries the infinite-scroll ref.
  const renderable = useMemo(
    () => activities.filter(isRenderableActivity),
    [activities]
  );

  // Handle delete comment wrapper
  const handleDeleteComment = async (commentId: string) => {
    const activityWithComment = activities.find(activity =>
      activity.comments?.some(comment => comment.id === commentId)
    );

    if (!activityWithComment) {
      return;
    }

    await onDeleteComment(activityWithComment.id, commentId);
  };

  // Filter toggle JSX
  const filterToggle = (
    <div className="flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={() => onFilterChange?.('all')}
        className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
          filterMode === 'all'
            ? 'bg-orange-500 text-white'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        }`}
        data-testid="activity-filter-all"
      >
        {t('dashboard:activityFeed.filterAll')}
      </button>
      <button
        type="button"
        onClick={() => onFilterChange?.('involved')}
        className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
          filterMode === 'involved'
            ? 'bg-orange-500 text-white'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        }`}
        data-testid="activity-filter-mine"
      >
        {t('dashboard:activityFeed.filterMine')}
      </button>
      <button
        type="button"
        onClick={() => onFilterChange?.('paidByMe')}
        className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
          filterMode === 'paidByMe'
            ? 'bg-orange-500 text-white'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        }`}
        data-testid="activity-filter-paid-by-me"
      >
        {t('dashboard:activityFeed.filterPaidByMe')}
      </button>
    </div>
  );

  // Loading skeleton
  if (initialLoading) {
    return (
      <div className="space-y-4" data-testid="activity-feed-loading">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          {showTitle && (
            <h2
              className="text-lg font-semibold text-gray-900 dark:text-white"
              data-testid="activity-feed-title-loading"
            >
              {t('dashboard:activityFeed.title')}
            </h2>
          )}
          {!showTitle && <div />}
          {filterToggle}
        </div>
        {[1, 2, 3].map(i => (
          <Card key={i} className="p-4" data-testid={`activity-skeleton-${i}`}>
            <div
              className="animate-pulse"
              data-testid="activity-loading-indicator"
            >
              {/* Compact two-column skeleton */}
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg shrink-0" />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                </div>
                <div className="text-right shrink-0 space-y-1.5">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-16 ml-auto" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12 ml-auto" />
                </div>
              </div>

              {/* Position indicator skeleton */}
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
              </div>

              {/* Interactions skeleton */}
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex justify-between">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24" />
                <div className="flex gap-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-8" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-8" />
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="activity-feed">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        {showTitle && (
          <h2
            className="text-lg font-semibold text-gray-900 dark:text-white"
            data-testid="activity-feed-title"
          >
            {t('dashboard:activityFeed.title')}
          </h2>
        )}
        {!showTitle && <div />}
        {filterToggle}
      </div>
      <div className="space-y-3 sm:space-y-4" data-testid="activity-feed-list">
        {renderable.length === 0 &&
        hasNextPage &&
        filterMode === 'all' &&
        onLoadMore ? (
          !isFetchingNextPage && (
            <div className="flex justify-center py-4">
              <Button
                variant="outline"
                onClick={onLoadMore}
                data-testid="activity-load-more"
              >
                {t('common:actions.loadMore')}
              </Button>
            </div>
          )
        ) : renderable.length === 0 &&
          !(filterMode !== 'all' && hasNextPage) ? (
          <Card className="p-6" data-testid="activity-empty-state-card">
            <div
              className="text-center py-8"
              data-testid="activity-empty-state"
            >
              <p
                className="text-gray-500 dark:text-gray-400"
                data-testid="activity-empty-message"
              >
                {t('dashboard:activityFeed.empty')}
              </p>
              <p
                className="text-sm text-gray-400 dark:text-gray-500 mt-1"
                data-testid="activity-empty-description"
              >
                {t('dashboard:activityFeed.emptyDescription')}
              </p>
            </div>
          </Card>
        ) : (
          renderable.map((activity, index) => {
            const isLastItem = index === renderable.length - 1;

            if (activity.type === 'expense') {
              return (
                <div
                  key={`${activity.type}-${activity.id}`}
                  ref={isLastItem ? lastActivityRef : null}
                  data-testid={`activity-item-${activity.id}`}
                >
                  <ExpenseCard
                    data-testid={`activity-expense-card-${activity.id}`}
                    expense={{
                      id: activity.id,
                      groupId: activity.groupId,
                      title: activity.description,
                      description: activity.description,
                      amount: activity.amount,
                      currency: activity.currency,
                      category: activity.category,
                      subcategory: activity.subcategory,
                      date: activity.expenseDate || activity.timestamp,
                      createdAt: activity.timestamp,
                      payers: activity.paidBy?.map(p => ({
                        userId: p.userId,
                        amount: p.amount,
                        userName:
                          p.userName ||
                          (p.userId === activity.userId
                            ? activity.user
                            : undefined),
                        profileImage: p.userProfileImage,
                        isSupporter: p.isSupporter,
                      })) || [
                        {
                          userId: activity.userId,
                          amount: activity.amount,
                          userName: activity.user,
                          profileImage: activity.userProfileImage,
                          isSupporter: activity.isSupporter,
                        },
                      ],
                      splits:
                        activity.splits?.map(s => ({
                          userId: s.userId,
                          amount: s.amount,
                          userName: s.userName,
                          profileImage: s.userProfileImage,
                          isSupporter: s.isSupporter,
                        })) || [],
                      aggregatedReactions: activity.reactions || [],
                      comments: activity.comments?.map(c => ({
                        id: c.id,
                        userId: c.userId,
                        name: c.user,
                        profileImage: c.profileImage,
                        isSupporter: c.isSupporter,
                        comment: c.text,
                        createdAt: c.createdAt,
                        updatedAt: c.updatedAt,
                        isEdited: c.isEdited,
                        parentCommentId: undefined,
                        replies: [],
                      })),
                      totalComments: activity.commentCount,
                      hasMoreComments: expandedComments.has(activity.id)
                        ? false
                        : activity.hasMoreComments,
                      originalAmount: activity.originalAmount,
                      originalCurrency: activity.originalCurrency,
                    }}
                    groupName={activity.groupName}
                    onAddReaction={emoji => onAddReaction(activity.id, emoji)}
                    onToggleReaction={(emoji, hasReacted) =>
                      onToggleReaction(activity.id, emoji, hasReacted)
                    }
                    onSubmitComment={text => onSubmitComment(activity.id, text)}
                    onEditComment={(commentId, text) =>
                      onEditComment(activity.id, commentId, text)
                    }
                    onDeleteComment={handleDeleteComment}
                    onLoadMoreComments={() => onLoadAllComments(activity.id)}
                    loadingReaction={loadingReaction[activity.id]}
                    loadingMoreComments={loadingComments[activity.id]}
                    variant="feed"
                    showPositionIndicator={true}
                    showSocialInteractions={true}
                    showSupporterBadges={showSupporterBadges}
                    isClickable={true}
                  />
                </div>
              );
            }

            if (isGroupEventItem(activity)) {
              return (
                <ActivityEventCard
                  key={`${activity.type}-${activity.id}`}
                  activity={activity}
                  isLast={isLastItem}
                  lastRef={lastActivityRef}
                  showSupporterBadge={showSupporterBadges}
                />
              );
            }

            // Settlement rendering
            return (
              <Card
                key={`${activity.type}-${activity.id}`}
                ref={isLastItem ? lastActivityRef : null}
                className="p-4 pr-5 pb-6 h-full flex flex-col"
                data-testid={`activity-settlement-${activity.id}`}
              >
                <div
                  className="flex items-start gap-2 sm:gap-3 mb-3"
                  data-testid="settlement-content"
                >
                  <Avatar
                    name={activity.user}
                    src={activity.userProfileImage}
                    size="md"
                    className="shrink-0"
                    data-testid="settlement-avatar"
                  />
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p
                      className="text-sm text-gray-900 dark:text-gray-100"
                      data-testid="settlement-description"
                    >
                      <span
                        className="font-medium"
                        data-testid="settlement-user-name"
                      >
                        {resolveUserName(activity.user, t)}
                        {showSupporterBadges && activity.isSupporter && (
                          <SupporterBadge className="ml-1" />
                        )}
                      </span>{' '}
                      {activity.description} {t('common:labels.in')}{' '}
                      <RouteChangeLink
                        href={`/groups/${activity.groupId}`}
                        className="text-orange-700 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 hover:underline"
                        data-testid="settlement-group-link"
                      >
                        {activity.groupName}
                      </RouteChangeLink>
                    </p>
                    <p
                      className="text-sm text-gray-600 dark:text-gray-400"
                      data-testid="settlement-date"
                    >
                      <RelativeTime date={activity.timestamp} />
                    </p>
                    <p
                      className="text-lg font-medium text-green-600 dark:text-green-400 mt-1"
                      data-testid="settlement-amount"
                    >
                      <CurrencyDisplay
                        amount={activity.amount}
                        currency={activity.currency}
                      />
                      <span className="ml-1">{t('common:labels.settled')}</span>
                    </p>
                  </div>
                </div>
              </Card>
            );
          })
        )}
        {/* Loading spinner */}
        {isFetchingNextPage && (
          <div
            className="flex items-center justify-center py-6"
            data-testid="activity-loading-more"
          >
            <div
              className="flex items-center gap-3 text-gray-500 dark:text-gray-400"
              data-testid="activity-spinner"
            >
              <div
                className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-orange-500 dark:border-t-orange-400 rounded-full animate-spin"
                data-testid="activity-loading-indicator"
              ></div>
              <span
                className="text-sm font-medium dark:text-gray-400"
                data-testid="loading-more-text"
              >
                {t('common:actions.loadingMore')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
